// playbackWorker.js - MIDI processing and playback worker

import { Midi } from '@tonejs/midi'; // Import Tone.js MIDI library for parsing MIDI data

// Data structures to store MIDI data and playback scheduling
let midiQueue = []; // Stores the parsed MIDI events to be scheduled
let notesInQueue = []; // Stores the notes to be played
let isPlaying = false; // Playback state
let tempo = 90; // Default tempo value to start with
let startTime = 0; // Start time for playback

// Function to handle incoming messages from the main thread
onmessage = function(event) {
    const { type, data, isReplay, bpm } = event.data;

    switch (type) {
        case 'LOAD_MIDI':
            tempo = bpm || tempo; // Set the tempo if provided
            console.log(`[Playback Worker] MIDI loaded at ${tempo} BPM`);
            // Only load new MIDI if there are no notes currently being played
            if (!isPlaying && notesInQueue.length === 0) {
                parseAndEnqueueMidi(data, isReplay || false);
                postMessage({ type: 'MIDI_LOADED' });
            } else {
                console.warn(`[Playback Worker] Attempted to load MIDI while notes are still in queue or already playing.`);
                //is this warning necessary?
            }
            break;

        case 'START_PLAYBACK':
            // Start playback only if we have notes to play and are not currently playing
            if (!isPlaying && notesInQueue.length > 0) {
                isPlaying = true;
                startTime = performance.now(); // Set the start time for playback
                console.log(`[Playback Worker] Starting playback at ${tempo} BPM`);
                scheduleNextNote();
            } else {
                console.warn(`[Playback Worker] Attempted to start playback but no notes available or already playing.`);
            }
            break;

        case 'REPLAY_MIDI':
            if (!isPlaying && data) {
                parseAndEnqueueMidi(data);
                isPlaying = true;
                startTime = performance.now(); // Set the start time for playback
                scheduleNextNote();
            } else {
                console.warn(`[Playback Worker] Attempted to replay MIDI but currently playing.`);
            }
            break;

        case 'STOP_PLAYBACK':
            stopPlayback();
            break;

        default:
            console.error(`[Playback Worker] Unknown message type: ${type}`); //figure out what to do with this
    }
};

// Function to parse and enqueue MIDI data
function parseAndEnqueueMidi(midiData, isReplay = false) {
    try {
        const midi = new Midi(midiData);
        const track = midi.tracks[0];
        console.log("[Playback Worker] Parsed MIDI:", midi);
        const ticksPerBeat = midi.header.ppq;
        const msPerTick = (60 * 1000 / tempo) / ticksPerBeat;

        // Reset notesInQueue to ensure correct timing
        notesInQueue = []; 

        track.notes.forEach((note) => {
            const note_velocity = scaleValue(note.velocity, [0, 1], [0, 127]);
            let timeMs = note.ticks * msPerTick;
            const note_duration = note.durationTicks * msPerTick;

            if (isReplay) {
                // Reset startTime for replay consistency
                startTime = performance.now();
            }

            notesInQueue.push({ note: note.midi, time: timeMs, duration: note_duration, velocity: note_velocity });
        });

        midiQueue = [...notesInQueue];
    } catch (error) {
        console.error("[Playback Worker] Error processing MIDI data:", error.message);
    }
}

// Schedule the next note to be played
function scheduleNextNote() {
    if (!isPlaying || notesInQueue.length === 0) {
        isPlaying = false;
        postMessage({ type: 'PLAYBACK_COMPLETE' });
        return;
    }

    const nextNote = notesInQueue[0]; // Peek at the next note
    const currentTime = performance.now();
    const timeUntilNextNote = nextNote.time - (currentTime - startTime);

    // Function to keep checking the timing for the next note
    function playNextFrame() {
        if (!isPlaying) return; // Stop if playback is paused

        const currentFrameTime = performance.now();
        if (currentFrameTime - startTime >= nextNote.time) {
            notesInQueue.shift(); // Remove the note from the queue
            postMessage({ type: 'PLAY_NOTE', data: nextNote }); // Send note data to main thread for playback
            scheduleNextNote(); // Schedule the next note after playing
        } else {
            requestAnimationFrame(playNextFrame); // Keep checking until it is time to play the note
        }
    }

    if (timeUntilNextNote <= 0) {
        // Immediately play the note if its time is already due
        notesInQueue.shift();
        postMessage({ type: 'PLAY_NOTE', data: nextNote });
        scheduleNextNote(); // Schedule the next note
    } else {
        // Start polling to check when it’s time to play the note
        requestAnimationFrame(playNextFrame);
    }
}

// Stop playback and clear queues
function stopPlayback() {
    isPlaying = false;
    notesInQueue = [];
    console.log('[Playback Worker] Playback stopped and queue cleared.');
}

// Utility function to scale a value from one range to another
function scaleValue(value, from, to) {
    let scale = (to[1] - to[0]) / (from[1] - from[0]);
    let capped = Math.min(from[1], Math.max(from[0], value)) - from[0];
    return (capped * scale + to[0]);
}
